import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import api from "../lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const ppToken = params.get("token");
  const isPaypal = params.get("provider") === "paypal" || (!!ppToken && !sessionId);
  const [status, setStatus] = useState("checking");
  const [balance, setBalance] = useState(0);
  const [info, setInfo] = useState({});

  useEffect(() => {
    const loadBalance = async (bookingId) => {
      try { const b = await api.get(`/bookings/${bookingId}`); setBalance(b.data.balance_due || 0); setInfo({ code: b.data.code, token: b.data.manage_token }); }
      catch (e) { if (process.env.NODE_ENV === "development") console.error("balance fetch failed", e); }
    };

    if (isPaypal && ppToken) {
      (async () => {
        try {
          const { data } = await api.post("/payments/paypal/capture", { order_id: ppToken });
          if (data.payment_status === "paid") { await loadBalance(data.booking_id); setStatus("paid"); return; }
          setStatus("pending");
        } catch (e) { setStatus("error"); }
      })();
      return;
    }

    if (!sessionId) { setStatus("error"); return; }
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid" || data.payment_status === "deposit_paid") {
          await loadBalance(data.booking_id);
          setStatus("paid"); return;
        }
        if (data.payment_status === "expired" || data.status === "failed") { setStatus("error"); return; }
      } catch (e) { if (process.env.NODE_ENV === "development") console.debug("payment status not ready, retrying", e?.message); }
      if (tries++ < 8) setTimeout(poll, 2000); else setStatus("pending");
    };
    poll();
  }, [sessionId, ppToken, isPaypal]);

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-5">
      <div className="card-lux p-10 max-w-md text-center" data-testid="payment-success">
        {status === "checking" && <><Loader2 className="animate-spin mx-auto text-rose" size={40} /><p className="mt-4 text-stone-500">Confirming your payment...</p></>}
        {status === "paid" && (
          <>
            <CheckCircle2 className="mx-auto text-green-600" size={56} />
            <h1 className="font-serif text-3xl text-forest mt-4">Booking Confirmed!</h1>
            <p className="text-stone-500 mt-3">Thank you — your stay is confirmed and a confirmation email is on its way. We can't wait to welcome you to the mountains.</p>
            {balance > 0 && <p className="text-sm text-amber-700 bg-amber-50 rounded-lg px-4 py-2 mt-4">Deposit received. A balance of <strong>${balance}</strong> is due before arrival.</p>}
            {info.code && <p className="text-sm text-stone-600 mt-4" data-testid="success-booking-code">Your booking code: <strong className="tracking-wide">{info.code}</strong><br /><span className="text-xs text-stone-400">Keep this to manage your booking anytime.</span></p>}
            <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-4 py-2 mt-3">The 30% deposit is non-refundable.</p>
            <div className="flex gap-3 justify-center mt-6">
              {info.token && <Link to={`/manage/${info.token}`} data-testid="success-manage-link" className="btn-dark inline-flex">Manage booking</Link>}
              <Link to="/" className="btn-primary inline-flex">Back to home</Link>
            </div>
          </>
        )}
        {status === "pending" && (
          <>
            <Loader2 className="mx-auto text-gold" size={48} />
            <h1 className="font-serif text-2xl text-forest mt-4">Payment processing</h1>
            <p className="text-stone-500 mt-3">Your payment is still processing. You'll receive a confirmation email shortly.</p>
            <Link to="/" className="btn-primary mt-6 inline-flex">Back to home</Link>
          </>
        )}
        {status === "error" && (
          <>
            <XCircle className="mx-auto text-red-500" size={48} />
            <h1 className="font-serif text-2xl text-forest mt-4">Something went wrong</h1>
            <p className="text-stone-500 mt-3">We couldn't confirm this payment. Please contact us and we'll help right away.</p>
            <Link to="/booking" className="btn-primary mt-6 inline-flex">Try again</Link>
          </>
        )}
      </div>
    </div>
  );
}
